const MAX_UNCERTAINTY_MS = 250;
const MAX_SAMPLE_AGE_MS = 120_000;
const DRIFT_MS_PER_SECOND = 0.1;
const MAX_SLEW_RATE = 0.05;

export interface ServerClockSample {
  readonly serverAtReceiptMs: number;
  readonly monotonicReceiptMs: number;
  readonly uncertaintyMs: number;
}

export interface ServerClockEstimate {
  readonly serverNowMs: number;
  readonly uncertaintyMs: number;
  readonly usable: boolean;
}

export class ServerClockEstimator {
  private serverAtAnchorMs = 0;
  private monotonicAnchorMs = 0;
  private sampleMonotonicMs = 0;
  private baseUncertaintyMs = Number.POSITIVE_INFINITY;
  private slewRemainingMs = 0;
  private initialized = false;
  private lastServerNowMs = 0;

  adopt(sample: ServerClockSample): 'initialized' | 'slewing' | 'recovery' {
    validateSample(sample);
    if (!this.initialized) {
      this.reset(sample);
      return 'initialized';
    }

    const current = this.estimate(sample.monotonicReceiptMs);
    const offsetMs = sample.serverAtReceiptMs - current.serverNowMs;
    if (Math.abs(offsetMs) + sample.uncertaintyMs > MAX_UNCERTAINTY_MS) {
      return 'recovery';
    }

    this.serverAtAnchorMs = current.serverNowMs;
    this.monotonicAnchorMs = sample.monotonicReceiptMs;
    this.sampleMonotonicMs = sample.monotonicReceiptMs;
    this.baseUncertaintyMs = sample.uncertaintyMs;
    this.slewRemainingMs = offsetMs;
    return 'slewing';
  }

  reset(sample: ServerClockSample): void {
    validateSample(sample);
    this.serverAtAnchorMs = sample.serverAtReceiptMs;
    this.monotonicAnchorMs = sample.monotonicReceiptMs;
    this.sampleMonotonicMs = sample.monotonicReceiptMs;
    this.baseUncertaintyMs = sample.uncertaintyMs;
    this.slewRemainingMs = 0;
    this.initialized = true;
    this.lastServerNowMs = sample.serverAtReceiptMs;
  }

  estimate(monotonicNowMs: number): ServerClockEstimate {
    if (!this.initialized || !Number.isFinite(monotonicNowMs)) {
      return {
        serverNowMs: this.lastServerNowMs,
        uncertaintyMs: Number.POSITIVE_INFINITY,
        usable: false,
      };
    }
    const elapsedMs = Math.max(0, monotonicNowMs - this.monotonicAnchorMs);
    const maximumCorrection = elapsedMs * MAX_SLEW_RATE;
    const appliedCorrection =
      Math.sign(this.slewRemainingMs) * Math.min(Math.abs(this.slewRemainingMs), maximumCorrection);
    const serverNowMs = Math.max(
      this.lastServerNowMs,
      this.serverAtAnchorMs + elapsedMs + appliedCorrection,
    );
    const outstandingCorrectionMs = Math.abs(this.slewRemainingMs - appliedCorrection);
    const sampleAgeMs = Math.max(0, monotonicNowMs - this.sampleMonotonicMs);
    const uncertaintyMs =
      this.baseUncertaintyMs +
      sampleAgeMs * (DRIFT_MS_PER_SECOND / 1_000) +
      outstandingCorrectionMs;
    this.lastServerNowMs = serverNowMs;
    return {
      serverNowMs,
      uncertaintyMs,
      usable: uncertaintyMs <= MAX_UNCERTAINTY_MS && sampleAgeMs <= MAX_SAMPLE_AGE_MS,
    };
  }
}

export function sampleServerClock(args: {
  readonly monotonicSentMs: number;
  readonly monotonicReceivedMs: number;
  readonly serverReceivedAtMs: number;
  readonly serverSentAtMs: number;
}): ServerClockSample | null {
  const { monotonicSentMs, monotonicReceivedMs, serverReceivedAtMs, serverSentAtMs } = args;
  if (
    ![monotonicSentMs, monotonicReceivedMs, serverReceivedAtMs, serverSentAtMs].every(
      Number.isFinite,
    ) ||
    monotonicReceivedMs < monotonicSentMs ||
    !Number.isSafeInteger(serverReceivedAtMs) ||
    !Number.isSafeInteger(serverSentAtMs) ||
    serverSentAtMs < serverReceivedAtMs
  ) {
    return null;
  }
  const networkRttMs =
    monotonicReceivedMs - monotonicSentMs - (serverSentAtMs - serverReceivedAtMs);
  if (networkRttMs < -10) return null;
  const boundedNetworkRttMs = Math.max(0, networkRttMs);
  return {
    serverAtReceiptMs: serverSentAtMs + boundedNetworkRttMs / 2,
    monotonicReceiptMs: monotonicReceivedMs,
    uncertaintyMs: boundedNetworkRttMs / 2 + 10,
  };
}

function validateSample(sample: ServerClockSample): void {
  if (
    !Number.isFinite(sample.serverAtReceiptMs) ||
    !Number.isFinite(sample.monotonicReceiptMs) ||
    !Number.isFinite(sample.uncertaintyMs) ||
    sample.uncertaintyMs < 0
  ) {
    throw new Error('Invalid server clock sample');
  }
}
